// CurrentReform — the "Your reform" trigger + modal (#65).
//
// A small link in the results-column header opens an on-demand modal that
// summarises the current reform: one row per changed parameter with its value
// and delta from baseline, the Copy-share-link control (relocated here from the
// header, #244), and a Reset-all affordance. Recognition over recall — the user
// sees "what reform am I looking at?" without scrolling the slider rail.
//
// Built on the native <dialog> element opened with showModal(): the browser
// gives focus management, Esc-to-close, scroll-lock, ::backdrop, ARIA, and
// top-layer rendering (above the simulating overlay, correct inside our iframe)
// for free — no dialog library, no z-index management. The only glue is syncing
// the `open` flag to showModal()/close().
//
// Rows derive from the share registry via the pure selectReformRows selector;
// the trigger's live count subscribes to the same store, so it updates as the
// user moves sliders without the modal being open.

import { useEffect, useRef, useState } from "react";
import { useShareStore } from "../state/share_store";
import { selectReformRows, groupReformRows } from "../state/reform_summary";
import { ShareButton } from "./inputs/share_button";

// Reuse the page-level "Reset all" path verbatim: a real click on that button
// fires BOTH the Shiny server reset (snaps the server-side sliders) and every
// React island's document-delegated reset signal (state/reset_signal). No new
// reset wiring — the modal just drives the existing button.
function triggerPageReset(): void {
  const btn = document.querySelector<HTMLElement>(
    '[data-testid="reset-all-button"]',
  );
  btn?.click();
}

export function CurrentReform(): JSX.Element {
  const controls = useShareStore((s) => s.controls);
  const rows = selectReformRows(controls);
  const groups = groupReformRows(rows);
  const count = rows.length;

  const [open, setOpen] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);

  // Drive the native dialog from the open flag. Guards against re-calling
  // showModal()/close() on an already-(open|closed) dialog — both throw, and
  // StrictMode double-invokes effects in dev.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    else if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <>
      <button
        type="button"
        className="beamm-reform-trigger"
        data-testid="reform-trigger"
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
      >
        <span>Your reform</span>
        {count > 0 ? (
          <span
            className="beamm-reform-trigger__count"
            data-testid="reform-trigger-count"
          >
            {count}
          </span>
        ) : null}
      </button>

      <dialog
        ref={dialogRef}
        className="beamm-reform-modal"
        data-testid="reform-modal"
        aria-labelledby="beamm-reform-modal-title"
        // `close` fires on Esc and on close(); keep React's flag in sync.
        onClose={() => setOpen(false)}
        // A click whose target is the dialog itself is a backdrop click (the
        // inner card catches clicks on the content); dismiss on it.
        onClick={(e) => {
          if (e.target === dialogRef.current) setOpen(false);
        }}
      >
        <div className="beamm-reform-modal__inner">
          <div className="beamm-reform-modal__header">
            <h2
              id="beamm-reform-modal-title"
              className="beamm-reform-modal__title"
            >
              Your current reform
            </h2>
            <button
              type="button"
              className="beamm-reform-modal__close"
              data-testid="reform-modal-close"
              aria-label="Close"
              onClick={() => setOpen(false)}
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>

          {count === 0 ? (
            <p className="beamm-reform-modal__empty" data-testid="reform-empty">
              No changes yet — adjust a parameter to build a reform.
            </p>
          ) : (
            <div className="beamm-reform-modal__groups">
              {groups.map((group) => (
                <section
                  key={group.instrument}
                  className="beamm-reform-group"
                  data-testid="reform-group"
                >
                  {group.instrument ? (
                    <h3 className="beamm-reform-group__heading">
                      {group.instrument}
                    </h3>
                  ) : null}
                  <ul className="beamm-reform-group__list">
                    {group.rows.map((row) => (
                      <li
                        key={row.key}
                        className="beamm-reform-row"
                        data-testid="reform-row"
                      >
                        <span className="beamm-reform-row__label">
                          {row.label}
                        </span>
                        <span className="beamm-reform-row__values">
                          <span className="beamm-reform-row__value">
                            {row.valueText}
                          </span>
                          {row.deltaText ? (
                            <span className="beamm-reform-row__delta">
                              {row.deltaText}
                            </span>
                          ) : null}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          )}

          <div className="beamm-reform-modal__actions">
            <ShareButton />
            <button
              type="button"
              className="beamm-reform-reset beamm-input-island__reset"
              data-testid="reform-reset"
              disabled={count === 0}
              onClick={() => {
                triggerPageReset();
                setOpen(false);
              }}
            >
              Reset all
            </button>
          </div>
        </div>
      </dialog>
    </>
  );
}
